import React, { useEffect, useRef, useState } from 'react';
import { MapContainer, TileLayer, useMap } from 'react-leaflet';
import PropertyMarker from './PropertyMarker';
import 'leaflet/dist/leaflet.css';
import L from 'leaflet';

// Fix para ícones padrão do Leaflet
delete (L.Icon.Default.prototype as any)._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png',
});

export interface PropertyLocation {
  id: string | number;
  position: [number, number];
  title: string;
  price: string;
  image: string;
}

interface PropertyMapProps {
  properties: PropertyLocation[];
  center?: [number, number];
  zoom?: number;
  onPropertyClick?: (propertyId: string | number) => void;
  className?: string;
  highlightedPropertyId?: string | number | null;
}

// Componente para controlar o scroll do mapa
const ScrollControl: React.FC<{ onScrollEnabled: (enabled: boolean) => void }> = ({ onScrollEnabled }) => {
  const map = useMap();

  useEffect(() => {
    const handleClick = () => {
      map.scrollWheelZoom.enable();
      onScrollEnabled(true);
    };

    const handleMouseLeave = () => {
      map.scrollWheelZoom.disable();
      onScrollEnabled(false);
    };

    map.on('click', handleClick);
    map.getContainer().addEventListener('mouseleave', handleMouseLeave);

    return () => {
      map.off('click', handleClick);
      map.getContainer().removeEventListener('mouseleave', handleMouseLeave);
    };
  }, [map, onScrollEnabled]);

  return null;
};

// Componente para garantir que o mapa carregue corretamente e se adapte ao zoom
const MapInvalidator: React.FC = () => {
  const map = useMap();

  useEffect(() => {
    // Invalida o tamanho do mapa após montagem para carregar tiles corretamente
    const initialTimer = setTimeout(() => {
      map.invalidateSize();
    }, 100);

    // Recalcular tamanho após eventos de zoom
    const handleZoomEnd = () => {
      setTimeout(() => {
        map.invalidateSize();
      }, 50);
    };

    // Recalcular tamanho após movimentação do mapa
    const handleMoveEnd = () => {
      map.invalidateSize();
    };

    // Adicionar listeners de eventos do Leaflet
    map.on('zoomend', handleZoomEnd);
    map.on('moveend', handleMoveEnd);

    // ResizeObserver para detectar mudanças de tamanho do container
    const container = map.getContainer();
    const resizeObserver = new ResizeObserver(() => {
      map.invalidateSize();
    });
    resizeObserver.observe(container);

    // Recalcular periodicamente (fallback para casos edge)
    const intervalId = setInterval(() => {
      map.invalidateSize();
    }, 2000);

    return () => {
      clearTimeout(initialTimer);
      map.off('zoomend', handleZoomEnd);
      map.off('moveend', handleMoveEnd);
      resizeObserver.disconnect();
      clearInterval(intervalId);
    };
  }, [map]);

  return null;
};

// Componente auxiliar para ajustar o mapa aos marcadores
const FitBounds: React.FC<{ properties: PropertyLocation[] }> = ({ properties }) => {
  const map = useMap();

  useEffect(() => {
    if (properties.length > 0) {
      const bounds = L.latLngBounds(properties.map((p) => p.position));
      map.fitBounds(bounds, { padding: [50, 50] });
    }
  }, [properties, map]);

  return null;
};

const PropertyMap: React.FC<PropertyMapProps> = ({
  properties,
  center = [-22.9068, -47.0653], // Campinas, SP
  zoom = 13,
  onPropertyClick,
  className = '',
  highlightedPropertyId,
}) => {
  const mapRef = useRef<L.Map | null>(null);
  const [scrollEnabled, setScrollEnabled] = useState(false);

  return (
    <div className={`relative w-full h-full ${className}`} style={{ minHeight: '100vh' }}>
      <MapContainer
        center={center}
        zoom={zoom}
        scrollWheelZoom={false}
        className="w-full h-full"
        ref={mapRef}
        style={{ height: '100%', width: '100%' }}
      >
        <TileLayer
          attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
        />
        
        {properties.map((property) => (
          <PropertyMarker
            key={property.id}
            id={property.id}
            position={property.position}
            title={property.title}
            price={property.price}
            image={property.image}
            onClick={() => onPropertyClick?.(property.id)}
            isHighlighted={highlightedPropertyId === property.id}
          />
        ))}

        {properties.length > 0 && <FitBounds properties={properties} />}
        <MapInvalidator />
        <ScrollControl onScrollEnabled={setScrollEnabled} />
      </MapContainer>

      {/* Mensagem de instrução quando scroll está desabilitado */}
      {!scrollEnabled && (
        <div className="absolute inset-0 pointer-events-none flex items-center justify-center">
          <div className="bg-white/90 backdrop-blur-sm px-4 py-2 rounded-lg shadow-lg pointer-events-auto">
            <p className="text-sm text-neutral-dark">Clique no mapa para habilitar o zoom</p>
          </div>
        </div>
      )}
    </div>
  );
};

export default PropertyMap;

